import { useState, type ChangeEvent } from 'react';
import { Button, Field } from '../../shared/ui/components';

/** La guardería recibe a menores de 3 años, contados con su fecha de nacimiento. */
const NURSERY_MAX_MONTHS = 36;

function ageInMonths(birthDate: string, today = new Date()): number | null {
  const [y, m, d] = birthDate.split('-').map(Number) as [number, number, number];
  if (!y || !m || !d) return null;
  return Math.max(0, (today.getFullYear() - y) * 12 + (today.getMonth() + 1 - m) - (today.getDate() < d ? 1 : 0));
}

const readAsDataUrl = (file: Blob) =>
  new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('No se pudo leer la foto.'));
    reader.readAsDataURL(file);
  });

/** La foto se achica en el aparato (máximo 800 px) para que suba rápido. */
async function photoDataUrl(file: File): Promise<string> {
  try {
    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, 800 / Math.max(bitmap.width, bitmap.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.round(bitmap.width * scale);
    canvas.height = Math.round(bitmap.height * scale);
    canvas.getContext('2d')!.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/jpeg', 0.85);
  } catch {
    return readAsDataUrl(file);
  }
}

/**
 * «¿Usará el servicio de guardería?» al registrar a un menor de 3 años, y si sí, lo que la
 * guardería pide como obligatorio. Lo usan Mi familia y Miembros.
 */
export function useNurseryIntake(idPrefix: string, birthDate: string | null, enabled = true) {
  const [answer, setAnswer] = useState<'si' | 'no' | ''>('');
  const [pickups, setPickups] = useState(1);
  const [photo, setPhoto] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  function pick(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    setPhoto(file);
    setPreview(null);
    if (file) readAsDataUrl(file).then(setPreview, () => undefined);
  }
  const months = birthDate ? ageInMonths(birthDate) : null;
  const asks = enabled && months !== null && months < NURSERY_MAX_MONTHS;
  const wanted = asks && answer === 'si';
  const id = (name: string) => `${idPrefix}-${name}`;

  /** Lo que falta antes de enviar, o null. */
  function missing(): string | null {
    if (asks && !answer) return 'Indica si usará la guardería.';
    if (wanted && !photo) return 'Agrega su foto.';
    return null;
  }

  async function read(form: FormData) {
    if (!wanted || !photo) return null;
    return {
      photo: await photoDataUrl(photo),
      allergies: String(form.get('allergies') ?? ''),
      instructions: String(form.get('instructions') ?? ''),
      medical: String(form.get('medical') ?? ''),
      contactPhone: String(form.get('contactPhone') ?? ''),
      pickups: Array.from({ length: pickups }, (_, i) => ({ name: String(form.get(`pickupName${i}`) ?? ''), relationship: String(form.get(`pickupRelationship${i}`) ?? '') })),
      diaper: form.get('diaper') === 'on',
      photoConsent: form.get('photoConsent') === 'on',
    };
  }

  function reset() {
    setAnswer('');
    setPickups(1);
    setPhoto(null);
    setPreview(null);
  }

  const fields = asks ? (
    <>
      <fieldset className="choices choices--row">
        <legend>¿Usará el servicio de guardería?</legend>
        <label className="choice"><input type="radio" name={id('useNursery')} checked={answer === 'si'} onChange={() => setAnswer('si')} /> <span>Sí</span></label>
        <label className="choice"><input type="radio" name={id('useNursery')} checked={answer === 'no'} onChange={() => setAnswer('no')} /> <span>No</span></label>
      </fieldset>
      {wanted ? (
        <>
          <p className="muted small">Para la guardería todo esto es obligatorio. Si no tiene alergias o cuidados, escribe «Ninguna» o «Ninguno».</p>
          <fieldset className="choices">
            <legend>Foto</legend>
            {preview ? <img className="photo-preview" src={preview} alt="Foto elegida" /> : null}
            <div className="actions actions--wrap">
              {/* Uno abre la cámara del celular; el otro, las fotos guardadas. */}
              <label className="btn btn--secondary">
                {photo ? 'Tomar otra foto' : 'Tomar foto'}
                <input className="visually-hidden" type="file" accept="image/*" capture="environment" onChange={pick} />
              </label>
              <label className="btn btn--secondary">
                Subir foto
                <input className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp" onChange={pick} />
              </label>
            </div>
          </fieldset>
          <Field label="Alergias" name="allergies" required maxLength={500} placeholder="Ninguna" />
          <div className="field">
            <label htmlFor={id('instructions')}>Cuidados especiales</label>
            <textarea id={id('instructions')} name="instructions" required maxLength={1000} rows={2} placeholder="Ninguno" />
          </div>
          <Field label="Medicamentos o condiciones médicas" name="medical" required maxLength={500} placeholder="Ninguno" />
          <Field label="Teléfono de contacto durante el servicio" name="contactPhone" type="tel" required minLength={7} maxLength={30} />
          <fieldset className="choices">
            <legend>Quién puede recogerlo, además de papá y mamá</legend>
            {Array.from({ length: pickups }, (_, i) => (
              <div key={i} className="form__row">
                <Field label="Nombre" name={`pickupName${i}`} required maxLength={120} />
                <Field label="Parentesco" name={`pickupRelationship${i}`} required maxLength={60} placeholder="Abuela" />
              </div>
            ))}
            {pickups < 5 ? (
              <Button type="button" variant="ghost" onClick={() => setPickups(pickups + 1)}>
                Agregar otra persona
              </Button>
            ) : null}
          </fieldset>
          <label className="check">
            <input type="checkbox" name="diaper" /> Usa pañal
          </label>
          <label className="check">
            <input type="checkbox" name="photoConsent" /> Doy permiso de que la guardería le tome fotos o video
          </label>
        </>
      ) : null}
    </>
  ) : null;

  return { fields, missing, read, reset };
}
